export enum InterestRegisterEntryType {
    ConflictOfInterest = 'ConflictOfInterest',
    GiftOrTravel = 'GiftOrTravel',
    EmploymentOrBoardParticipation = 'EmploymentOrBoardParticipation',
    SecuritiesTrading = 'SecuritiesTrading',
    InterDepartmentTrading = 'InterDepartmentTrading',
    MyEntries = 'MyEntries',
    AllEntries = 'AllEntries',
}

export enum InterestRegisterEntryStatus {
    Submitted = 'Submitted',
    Reviewed = 'Reviewed',
}

export interface InterestRegisterEntryTypeConfig {
    type: InterestRegisterEntryType;
    title: string;
    description: string;
    actionText: string;
    icon: string;
    accentClass?: string;
    accentColor: string;
}

export interface InterestRegisterAttachment {
    id: string;
    fileName: string;
    fileSize: number;
    contentType?: string;
}

export interface InterestRegisterListItem {
    id: string;
    type: InterestRegisterEntryType;
    typeLabel: string;
    createdDate: string;
    createdByName: string;
    status: InterestRegisterEntryStatus;
    isReviewed: boolean;
    reviewedDate?: string;
    reviewedBy?: number;
    reviewedByName?: string;
    hasAttachments: boolean;
}

export interface InterestRegisterDetail extends InterestRegisterListItem {
    formData: Record<string, unknown>;
    attachments: InterestRegisterAttachment[];
}

export interface CreateInterestRegisterEntryReq {
    type: InterestRegisterEntryType;
    formData: Record<string, unknown>;
    attachments: File[];
}

export interface ConflictOfInterestFormData {
    area: string;
    projectType: string;
    description: string;
    conflictingRelations: string;
    measuresTaken: string;
}

export interface GiftOrTravelFormData {
    type: 'Gift' | 'Invitation' | 'Travel';
    offeredBy: string;
    description: string;
    date: string;
    estimatedValue: number;
}

export interface EmploymentOrBoardParticipationFormData {
    participationType: 'Employment' | 'EmploymentActivity' | 'BoardParticipation' | 'InvestmentCouncil' | 'InterestCouncil';
    memberRole?: string;
    startOrEnd: 'Start' | 'End';
    isRelatedToLsrRole: boolean;
    compensation?: number;
    date: string;
    companyName: string;
    companyType?: string;
    otherRelations?: string;
    otherDescription?: string;
}

export interface SecuritiesTradingFormData {
    transactionDate: string;
    transactionType: 'Buy' | 'Sell';
    transactionOwnerName?: string;
    financialInstrumentName: string;
    isPublicOffering: boolean;
    nominalValue?: number;
    transactionPrice?: number;
    otherInformation?: string;
}

export interface InterDepartmentTradingFormData {
    transactionDate: string;
    buyer: string;
    seller: string;
    financialInstrumentName: string;
    nominalValue: number;
    yieldOrPrice: number;
    transactionTermsReference: string;
    interestAssessment?: string;
}

export const INTEREST_REGISTER_ENTRY_TYPES: InterestRegisterEntryTypeConfig[] = [
    {
        type: InterestRegisterEntryType.SecuritiesTrading,
        title: 'Viðskipti með fjármálagerninga',
        description: 'Skráðu viðskipti með fjármálagerninga.',
        actionText: 'Skrá viðskipti',
        icon: 'heroicons_outline:chart-bar',
        accentClass: 'interest-register-card--green',
        accentColor: '34, 197, 94',
    },
    {
        type: InterestRegisterEntryType.ConflictOfInterest,
        title: 'Skráning hagsmunaárekstra',
        description: 'Skráðu hagsmunaárekstur og hvaða ráðstafana var gripið til.',
        actionText: 'Skrá hagsmunaárekstur',
        icon: 'heroicons_outline:exclamation-triangle',
        accentClass: 'interest-register-card--amber',
        accentColor: '245, 158, 11',
    },
    {
        type: InterestRegisterEntryType.GiftOrTravel,
        title: 'Gjafir, boð og starfstengdar boðsferðir',
        description: 'Skráðu gjafir, boð eða starfstengdar boðsferðir.',
        actionText: 'Skrá gjöf, boð eða boðsferð',
        icon: 'heroicons_outline:gift',
        accentClass: 'interest-register-card--blue',
        accentColor: '59, 130, 246',
    },
    {
        type: InterestRegisterEntryType.EmploymentOrBoardParticipation,
        title: 'Atvinnuþátttaka, atvinnustarfsemi og stjórnarseta',
        description: 'Skráðu launuð störf, atvinnustarfsemi, stjórnarsetu eða setu í ráðum.',
        actionText: 'Skrá þátttöku',
        icon: 'heroicons_outline:briefcase',
        accentClass: 'interest-register-card--purple',
        accentColor: '124, 58, 237',
    },
    {
        type: InterestRegisterEntryType.InterDepartmentTrading,
        title: 'Viðskipti milli deilda LSR',
        description: 'Skráðu viðskipti milli deilda sjóðsins og forsendur viðskiptakjara.',
        actionText: 'Skrá viðskipti',
        icon: 'heroicons_outline:arrows-right-left',
        accentClass: 'interest-register-card--red',
        accentColor: '185, 28, 28',
    },
];

export const MY_INTEREST_REGISTER_ENTRIES_CARD: InterestRegisterEntryTypeConfig = {
    type: InterestRegisterEntryType.MyEntries,
    title: 'Mínar skráningar',
    description: 'Hér færðu yfirlit yfir skráningar sem þú hefur sent inn.',
    actionText: 'Skoða mínar skráningar',
    icon: 'heroicons_outline:list-bullet',
    accentClass: 'interest-register-card--slate',
    accentColor: '100, 116, 139',
};

export const ALL_INTEREST_REGISTER_ENTRIES_CARD: InterestRegisterEntryTypeConfig = {
    type: InterestRegisterEntryType.AllEntries,
    title: 'Allar skráningar',
    description: 'Læst yfirlit fyrir aðila með sérstakan aðgang að Hagsmunaskrá.',
    actionText: 'Skoða allar skráningar',
    icon: 'heroicons_outline:lock-open',
    accentClass: 'interest-register-card--slate',
    accentColor: '100, 116, 139',
};
